import { useEffect, useState } from 'react';
import { requestMembershipRefund } from '../../../api/user';
import '../../../css/MembershipRefundModal.css';

const SUBSCRIPTION_TYPE_LABEL = {
  basic: '월간 구독',
  standard: '월간 구독',
  premium: '월간 구독',
  free: '구독 없음',
  owner: '관리자',
};

function formatWithComma(value) {
  const digits = String(value).replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString();
}

function parseComma(value) {
  return Number(String(value).replace(/,/g, '')) || 0;
}

/**
 * 멤버십 환불 요청 모달
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {{ userId, name, subscriptionInfo: { type, price }, refundInfo: { originalAmount, refundableAmount } }} props.userData
 * @param {Function} props.onClose
 * @param {Function} props.onSuccess
 */
function MembershipRefundModal({ isOpen, userData, onClose, onSuccess }) {
  const [refundAmountRaw, setRefundAmountRaw] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRefundAmountRaw('');
      setReason('');
      setConfirmed(false);
      setSubmitting(false);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen || !userData) return null;

  const { userId, name } = userData;
  const subscriptionInfo = userData.subscriptionInfo || {};
  const refundInfo = userData.refundInfo || {};
  const subscriptionLabel =
    SUBSCRIPTION_TYPE_LABEL[subscriptionInfo.type] || subscriptionInfo.type || '-';
  const priceFormatted =
    subscriptionInfo.price != null
      ? `${Number(subscriptionInfo.price).toLocaleString()}원`
      : '-';

  const refundAmount = parseComma(refundAmountRaw);
  const refundableAmount = refundInfo.refundableAmount ?? 0;
  const finalBalance = refundableAmount - refundAmount;
  const amountError =
    refundAmount > 0 && refundAmount > refundableAmount
      ? `환불 가능 금액(${refundableAmount.toLocaleString()}원)을 초과합니다.`
      : '';

  const canSubmit =
    refundAmount >= 1 &&
    refundAmount <= refundableAmount &&
    reason.trim().length >= 1 &&
    confirmed &&
    !submitting;

  const handleAmountChange = (e) => {
    setRefundAmountRaw(formatWithComma(e.target.value));
  };

  const handleOverlayClick = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      const refundType = refundAmount === refundInfo.originalAmount ? 'full' : 'partial';
      await requestMembershipRefund({
        userId,
        refundType,
        refundedAmount: refundAmount,
        reason: reason.trim(),
      });
      onClose();
      onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.data?.detail ||
        err?.response?.data?.detail ||
        err?.message ||
        '환불 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="membership-refund-modal-overlay"
      onClick={handleOverlayClick}
      data-testid="membership-refund-modal-overlay"
    >
      <div
        className="membership-refund-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="membership-refund-modal"
      >
        <div className="membership-refund-modal-header">
          <h3>멤버십 환불 요청</h3>
          <button
            className="membership-refund-modal-close-btn"
            onClick={() => { if (!submitting) onClose(); }}
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        <div className="membership-refund-modal-body">
          <div className="membership-refund-info-grid">
            <div className="membership-refund-info-row">
              <span className="membership-refund-info-label">사용자</span>
              <span className="membership-refund-info-value">{name}</span>
            </div>
            <div className="membership-refund-info-row">
              <span className="membership-refund-info-label">멤버십 종류</span>
              <span className="membership-refund-info-value">{subscriptionLabel}</span>
            </div>
            <div className="membership-refund-info-row">
              <span className="membership-refund-info-label">멤버십 금액</span>
              <span className="membership-refund-info-value">{priceFormatted}</span>
            </div>
            <div className="membership-refund-info-row">
              <span className="membership-refund-info-label">환불 가능 금액</span>
              <span className="membership-refund-info-value">{refundableAmount.toLocaleString()}원</span>
            </div>
          </div>

          <div className="membership-refund-field">
            <label className="membership-refund-field-label">
              환불 금액 <span className="membership-refund-required">*</span>
            </label>
            <input
              type="text"
              className={`membership-refund-amount-input${amountError ? ' membership-refund-amount-input--error' : ''}`}
              value={refundAmountRaw}
              onChange={handleAmountChange}
              placeholder="환불 금액을 입력하세요"
              disabled={submitting}
              data-testid="refund-amount-input"
            />
            {amountError && (
              <span className="membership-refund-field-error" data-testid="amount-error">
                {amountError}
              </span>
            )}
          </div>

          <div className="membership-refund-field">
            <label className="membership-refund-field-label">
              환불 사유 <span className="membership-refund-required">*</span>
            </label>
            <textarea
              className="membership-refund-reason-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              placeholder="환불 사유를 입력해주세요"
              disabled={submitting}
              rows={3}
              data-testid="reason-textarea"
            />
            <span className="membership-refund-char-count">{reason.length}/200자 이내</span>
          </div>

          <div className="membership-refund-confirm-wrap">
            <input
              type="checkbox"
              id="membership-refund-confirm-checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={submitting}
              data-testid="confirm-checkbox"
            />
            <label
              htmlFor="membership-refund-confirm-checkbox"
              className="membership-refund-confirm-label"
            >
              <span className="membership-refund-custom-checkbox" />
              [확인] 환불요청 버튼을 누르면 즉시 환불이 접수되며, 취소가 불가합니다.
            </label>
          </div>

          {error && (
            <div className="membership-refund-error-msg" data-testid="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="membership-refund-modal-footer">
          <div className="membership-refund-balance-info">
            <span className="membership-refund-balance-text">
              최종 잔액 : {refundableAmount === 0 ? '-' : `${finalBalance.toLocaleString()}원`}
            </span>
            {refundAmount > 0 && !amountError && (
              <span className={finalBalance === 0 ? "membership-refund-guide-full" : "membership-refund-guide-partial"}>
                {finalBalance === 0
                  ? "전액 환불 건. 환불 요청을 눌러주세요."
                  : "잔액이 존재합니다. 금액을 확인 후 진행해 주세요."}
              </span>
            )}
          </div>
          <div className="membership-refund-footer-btns">
            <button
              className="membership-refund-cancel-btn"
              onClick={() => { if (!submitting) onClose(); }}
              disabled={submitting}
            >
              취소
            </button>
            <button
              className="membership-refund-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />{' '}
                  처리 중...
                </>
              ) : (
                '환불 요청'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MembershipRefundModal;
